import { makeVar } from '@apollo/client'
import { NoteType } from 'models/projectNote'

/** Pre-fill data encoded in the URL when "Create Task" is triggered from the hover action bar */
export interface WorkspaceTasksPrefill {
    /** NoteType to pre-select in the create dialog */
    noteType: NoteType
    /** contentItemId of the hovered article / paragraph */
    contentItemId?: string
    /** projectElementId of the selected element (section, product type, etc.) */
    projectElementId?: string
    /** Human-readable breadcrumb shown as the anchor reference */
    projectElementDescription?: string
    /** true when hovered element is an article (vs paragraph) */
    isArticle?: boolean
    /** Mirrors classic CreateProjectNoteInput.isProductTypeProperty */
    isProductTypeProperty?: boolean
    /** Mirrors classic CreateProjectNoteInput.projectProductFamilyId */
    projectProductFamilyId?: string
}

/**
 * Set of contentItemIds that have at least one task attached.
 * Populated on page load from GetProjectNotesQuery so the document
 * can show a task-indicator icon next to articles/paragraphs.
 */
export const taskContentItemIds = makeVar<ReadonlySet<string>>(new Set())

/** Call whenever the in-memory notes list changes so the indicator stays in sync */
export const syncTaskContentItemIds = (notes: { contentItemId?: string | null }[]) => {
    taskContentItemIds(
        new Set(notes.map((n) => n.contentItemId).filter((id): id is string => !!id))
    )
}

/**
 * localStorage key used to push note state from the tasks window to the workspace.
 * The workspace listens to storage events on this key and updates reactive vars
 * directly — no extra API round-trip needed.
 */
export const TASK_STATE_KEY = 'sp_task_state'

/**
 * Broadcast the current in-memory notes state to the workspace window.
 * Called from useProjectTasks whenever the local notes array changes so
 * the task indicator icons and count badge stay in sync without a refetch.
 */
export const broadcastTaskState = (
    projectId: string,
    elementId: string,
    notes: { contentItemId?: string | null }[]
) => {
    const contentItemIds = notes
        .map((n) => n.contentItemId)
        .filter((id): id is string => !!id)
    localStorage.setItem(
        TASK_STATE_KEY,
        JSON.stringify({
            projectId,
            elementId,
            contentItemIds,
            totalCount: notes.length,
            elementLevelCount: notes.filter((n) => !n.contentItemId).length,
        })
    )
}
